import React, { useEffect, useMemo, useRef, useState } from "react";

import { DateRange } from "react-day-picker";

import dayjs from "dayjs";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  Calendar,
  ChevronDown,
  Download,
  Info,
  Loader2,
  Trash2,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useAcceptedSalesNotes } from "~/hooks/useAcceptedSalesNotes";
import { usePorFacturarSalesNotes } from "~/hooks/usePorFacturarSalesNotes";
import { usePrepaidSalesNotes } from "~/hooks/usePrepaidSalesNotes";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { cn } from "~/lib/utils";
import { useStatusSummary } from "~/pages/purchase-orders/hooks/useStatusSummary";
import { ClientInfo } from "~/pages/purchase-orders/hooks/utils/clientUtils";
import { exportKanbanColumnToExcel } from "~/services/kanbanExportService";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet } from "~/utils/iva";
import { getDisplayId } from "~/utils/salesRequestDisplayUtils";

// Configure dayjs
dayjs.extend(relativeTime);
dayjs.locale("es");

// Status translation utility
const getStatusLabel = (status: string) => {
  const statusLabels = {
    pending: "Autorización Cobranza",
    pending_pricing: "Autorización por Precio",
    pending_credit: "Crédito Insuficiente",
    accepted: "Aprobada",
    prepaid: "Anticipado Pdte Pago",
    payment_to_check: "Validación Pago",
    por_facturar: "Por Facturar",
    in_transit: "En Tránsito",
    delivered: "Entregado",
    rejected: "Rechazada",
    test: "Test",
  };
  return statusLabels[status as keyof typeof statusLabels] || status;
};

interface KanbanViewProps {
  salesRequests: SalesRequest[];
  isLoading?: boolean;
  clientNames: Record<string, string>;
  clientInfo?: Record<string, ClientInfo>;
  paymentCounts?: Record<string, number>;
  clientSalesConditions?: Record<string, string>;
  salesRequestItems?: Record<string, SalesRequestItem[]>;
  /** Sales request id → destination status; a card is highlighted only in the
   *  column matching its moved-to status (so it animates on arrival, not before). */
  movedStatuses?: Map<string, string>;
  onOrderClick: (order: SalesRequest) => void;
  viewAllSellers?: boolean;
  dateRange?: DateRange;
  getSellerInfo?: (sellerId: string) => {
    name: string;
    lastname: string;
    email: string;
  };
  getTransportistaInfo?: (transportistaId: string) => {
    name: string;
    lastname: string;
    phone?: string;
  };
}

// Configuration for columns with lazy loading
const LAZY_LOAD_CONFIG = {
  // High priority workflow columns - show more initially (ordered by workflow)
  pending_pricing: { initial: 20, increment: 5 }, // Pendiente de autorización (first step)
  pending: { initial: 20, increment: 5 }, // Autorización cobranza
  prepaid: { initial: 100, increment: 10 }, // Anticipado pendiente de pago - show more to avoid missing items
  payment_to_check: { initial: 20, increment: 5 }, // Validación pago
  accepted: { initial: 20, increment: 10 },
  por_facturar: { initial: 20, increment: 10 }, // Orders ready for factura generation
  in_transit: { initial: 20, increment: 5 },
  facturado: { initial: 20, increment: 10 },

  // Lower priority columns - show fewer initially
  delivered: { initial: 3, increment: 5 },
  rejected: { initial: 1, increment: 3 }, // Deprioritized - show minimal initially
  test: { initial: 1, increment: 3 }, // Deprioritized - show minimal initially
};

// Column tooltip descriptions
const COLUMN_TOOLTIPS = {
  pending_pricing:
    "El equipo de ventas debe aprobar los precios que están fuera del rango sugerido antes de continuar",
  pending:
    "El equipo de cobranza debe revisar y aprobar estas notas de venta para clientes con condiciones de crédito",
  prepaid:
    "Clientes con TRANSFERENCIA ANTICIPADA esperando que el vendedor registre el pago recibido",
  payment_to_check:
    "Pagos pendientes de validación por cobranza (incluye órdenes prepago con pagos registrados)",
  accepted:
    "Notas aprobadas sin factura. La factura se genera manualmente desde aquí",
  por_facturar: "Notas aprobadas para facturación. Listas para generar factura",
  facturado:
    "Notas con factura generada pendientes de asignación de transportista",
  in_transit: "Productos despachados y en camino al cliente",
  delivered: "Productos entregados exitosamente al cliente",
  rejected:
    "Notas rechazadas por diversos motivos durante el proceso de aprobación",
  test: "Notas de prueba para testing del sistema",
};

/**
 * KanbanView Component
 *
 * Displays sales notes in a kanban board with workflow status columns.
 *
 * Column Visibility by Role (based on workflow diagram):
 * - Transportistas: "En Tránsito" and "Entregado"
 * - Logística: "Facturado", "Transito", "Entregado", "Rechazado"
 * - Cobranza: "Autorización Cobranza", "Anticipado Pdte Pago", "Validación Pago", "Aprobadas Cobranza", "Facturado", "Transito", "Entregado", "Rechazado"
 * - Facturación: "Autorización Precio", "Aprobadas Cobranza", "Facturado", "Transito", "Entregado", "Rechazado"
 * - All other roles (sellers, admin, etc.): See ALL 10 workflow status columns
 *
 * Data Filtering:
 * - Sellers: Only see sales notes they created, but across ALL status columns
 * - Other roles: See relevant sales notes based on their workflow responsibilities
 */
const KanbanView: React.FC<KanbanViewProps> = ({
  salesRequests,
  isLoading = false,
  clientNames,
  clientInfo,
  paymentCounts,
  clientSalesConditions,
  movedStatuses,
  onOrderClick,
  viewAllSellers = false,
  dateRange,
  getSellerInfo,
  getTransportistaInfo,
}) => {
  // State for tracking how many items to show in lazy-loaded columns
  const [visibleCounts, setVisibleCounts] = useState({
    pending_pricing: LAZY_LOAD_CONFIG.pending_pricing.initial,
    pending: LAZY_LOAD_CONFIG.pending.initial,
    prepaid: LAZY_LOAD_CONFIG.prepaid.initial,
    payment_to_check: LAZY_LOAD_CONFIG.payment_to_check.initial,
    accepted: LAZY_LOAD_CONFIG.accepted.initial,
    por_facturar: LAZY_LOAD_CONFIG.por_facturar.initial,
    facturado: LAZY_LOAD_CONFIG.facturado.initial,
    in_transit: LAZY_LOAD_CONFIG.in_transit.initial,
    delivered: LAZY_LOAD_CONFIG.delivered.initial,
    rejected: LAZY_LOAD_CONFIG.rejected.initial,
    test: LAZY_LOAD_CONFIG.test.initial,
  });

  // State for En Tránsito column filter
  const [inTransitFilter, setInTransitFilter] = useState<
    "all" | "paid" | "unpaid"
  >("all");

  // State for tracking export in progress
  const [exportingStatus, setExportingStatus] = useState<string | null>(null);

  // Get user role from profile store
  const { id: userId, role: userRole } = useProfileStore();
  const isTransportista = userRole === "transportista";
  const isFacturacion = userRole === "facturacion";
  const isLogistica = userRole === "logistica";
  const isCobranza = userRole === "cobranza";

  // Custom hook for prepaid sales notes with independent pagination
  const {
    prepaidNotes,
    prepaidClientNames,
    isLoading: isPrepaidLoading,
    isLoadingMore: isPrepaidLoadingMore,
    hasMore: hasPrepaidMore,
    hasLoaded: hasPrepaidLoaded,
    fetchPrepaidNotes,
    loadMorePrepaidNotes,
  } = usePrepaidSalesNotes(viewAllSellers);

  // Custom hook for accepted sales notes with independent pagination
  const {
    acceptedNotes,
    acceptedClientNames,
    isLoading: isAcceptedLoading,
    isLoadingMore: isAcceptedLoadingMore,
    hasMore: hasAcceptedMore,
    hasLoaded: hasAcceptedLoaded,
    fetchAcceptedNotes,
    loadMoreAcceptedNotes,
  } = useAcceptedSalesNotes(viewAllSellers);

  // Custom hook for por_facturar sales notes with independent pagination
  const {
    porFacturarNotes,
    porFacturarClientNames,
    isLoading: isPorFacturarLoading,
    isLoadingMore: isPorFacturarLoadingMore,
    hasMore: hasPorFacturarMore,
    hasLoaded: hasPorFacturarLoaded,
    fetchPorFacturarNotes,
    loadMorePorFacturarNotes,
  } = usePorFacturarSalesNotes(viewAllSellers);

  // Hook for status summary data (includes "facturado" virtual status from DB)
  const { statusSummary } = useStatusSummary({ dateRange, viewAllSellers });

  // Synchronized initial reveal: every column keeps showing skeletons until
  // each data source the current role's columns use has finished its first
  // fetch, then all cards fade in together (later refetches keep the normal
  // per-column loading behavior).
  const requiredSourcesLoaded = isTransportista
    ? true
    : isLogistica
      ? hasPorFacturarLoaded
      : isFacturacion
        ? hasAcceptedLoaded && hasPorFacturarLoaded
        : hasPrepaidLoaded && hasAcceptedLoaded && hasPorFacturarLoaded;

  // Latch: once every source has settled, the board stays revealed.
  const hasBoardLoadedRef = useRef(false);
  if (!isLoading && requiredSourcesLoaded) {
    hasBoardLoadedRef.current = true;
  }
  const hasBoardLoaded = hasBoardLoadedRef.current;

  // Top horizontal scrollbar: a proxy strip above the board mirrors the
  // board's scroll width and both stay in sync (the board's own bottom
  // scrollbar is hidden).
  const topScrollRef = useRef<HTMLDivElement>(null);
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const [boardScrollWidth, setBoardScrollWidth] = useState(0);

  useEffect(() => {
    const board = boardScrollRef.current;
    if (!board) return;
    const updateWidth = () => setBoardScrollWidth(board.scrollWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(board);
    // Columns are md:w-auto, so their width changes as cards load in.
    Array.from(board.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [hasBoardLoaded]);

  const syncScroll = (
    source: HTMLDivElement | null,
    target: HTMLDivElement | null
  ) => {
    if (source && target && target.scrollLeft !== source.scrollLeft) {
      target.scrollLeft = source.scrollLeft;
    }
  };

  // Facturado: in_transit WITHOUT assigned transportista
  const facturadoOrders = useMemo(
    () =>
      salesRequests.filter(
        (order) =>
          order.status === "in_transit" && !order.assigned_transportista
      ),
    [salesRequests]
  );

  // Helper function to get status data from summary
  const getStatusData = (status: string) => {
    const data = statusSummary[status];
    return {
      count: data?.count || 0,
      totalAmount: data?.total_amount || 0,
      priceWithIva: data?.price_with_iva || 0,
    };
  };

  // Merge client names from all sources
  const mergedClientNames = useMemo(
    () => ({
      ...clientNames,
      ...prepaidClientNames,
      ...acceptedClientNames,
      ...porFacturarClientNames,
    }),
    [
      clientNames,
      prepaidClientNames,
      acceptedClientNames,
      porFacturarClientNames,
    ]
  );

  // Fetch prepaid notes on component mount - only once
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchPrepaidNotes();
    }
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array to run only once

  // Fetch accepted notes on component mount - only once
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchAcceptedNotes();
    }
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array to run only once

  // Fetch por_facturar notes on component mount - only once
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchPorFacturarNotes();
    }
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array to run only once

  // Function to load more items for a specific column
  const loadMore = (columnKey: keyof typeof visibleCounts) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [columnKey]: prev[columnKey] + LAZY_LOAD_CONFIG[columnKey].increment,
    }));
  };

  // Export column data to Excel (fetches all records from DB)
  const handleExportColumn = async (status: string, columnTitle: string) => {
    setExportingStatus(status);
    try {
      await exportKanbanColumnToExcel({
        status,
        columnTitle,
        dateRange,
        viewAllSellers,
        userRole,
        userId,
      });
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExportingStatus(null);
    }
  };

  // Group sales requests by status - MEMOIZED to prevent infinite rerenders
  const pendingOrders = useMemo(
    () => salesRequests.filter((order) => order.status === "pending"),
    [salesRequests]
  );

  const pendingPricingOrders = useMemo(
    () => salesRequests.filter((order) => order.status === "pending_pricing"),
    [salesRequests]
  );

  // Use custom prepaid notes from the hook, filtered by payment status
  const prepaidOrders = useMemo(
    () =>
      prepaidNotes.filter(
        (order) => !paymentCounts?.[order.id] || paymentCounts[order.id] === 0
      ),
    [prepaidNotes, paymentCounts]
  );

  const prepaidWithPayments = useMemo(
    () =>
      prepaidNotes.filter(
        (order) => paymentCounts?.[order.id] && paymentCounts[order.id] > 0
      ),
    [prepaidNotes, paymentCounts]
  );

  // Payment validation column includes both payment_to_check status and prepaid with payments
  const paymentToCheckOrders = useMemo(
    () => salesRequests.filter((order) => order.status === "payment_to_check"),
    [salesRequests]
  );

  const allPaymentValidationOrders = useMemo(
    () => [...paymentToCheckOrders, ...prepaidWithPayments],
    [paymentToCheckOrders, prepaidWithPayments]
  );

  // Accepted orders (no filtering needed - all accepted orders stay in accepted column)
  const acceptedOrders = useMemo(
    () => salesRequests.filter((order) => order.status === "accepted"),
    [salesRequests]
  );

  // Por Facturar orders - now using dedicated hook for independent pagination
  // porFacturarNotes comes from usePorFacturarSalesNotes hook

  // En Tránsito: in_transit WITH assigned transportista
  const inTransitOrders = useMemo(
    () =>
      salesRequests.filter(
        (order) => order.status === "in_transit" && order.assigned_transportista
      ),
    [salesRequests]
  );

  // Get sales conditions hook for sorting
  const { getConditionById } = useSalesConditions();

  // Sort and filter in-transit orders
  const sortedAndFilteredInTransitOrders = useMemo(() => {
    let filteredOrders = [...inTransitOrders];

    // Apply filter based on paid status
    if (inTransitFilter === "paid") {
      filteredOrders = filteredOrders.filter((order) => order.paid);
    } else if (inTransitFilter === "unpaid") {
      filteredOrders = filteredOrders.filter((order) => !order.paid);
    }

    // Sort the filtered orders to prioritize unpaid orders with payments first
    return filteredOrders.sort((a, b) => {
      // Get payment and paid status
      const aHasPayments = (paymentCounts?.[a.id] || 0) > 0;
      const bHasPayments = (paymentCounts?.[b.id] || 0) > 0;
      const aPaid = a.paid;
      const bPaid = b.paid;

      // Priority 1: Unpaid orders with payments come first
      const aUnpaidWithPayments = !aPaid && aHasPayments;
      const bUnpaidWithPayments = !bPaid && bHasPayments;

      if (aUnpaidWithPayments && !bUnpaidWithPayments) {
        return -1;
      }
      if (bUnpaidWithPayments && !aUnpaidWithPayments) {
        return 1;
      }

      // Priority 2: Orders with payments come next
      if (aHasPayments && !bHasPayments) {
        return -1;
      }
      if (bHasPayments && !aHasPayments) {
        return 1;
      }

      // Priority 3: Among orders with same payment status, sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [inTransitOrders, paymentCounts, inTransitFilter]);

  const deliveredOrders = useMemo(
    () => salesRequests.filter((order) => order.status === "delivered"),
    [salesRequests]
  );

  const rejectedOrders = useMemo(
    () => salesRequests.filter((order) => order.status === "rejected"),
    [salesRequests]
  );

  const testOrders = useMemo(
    () => salesRequests.filter((order) => order.status === "test"),
    [salesRequests]
  );

  // Sort delivered orders to prioritize sales requests with payments at the top
  const sortedDeliveredOrders = useMemo(() => {
    return [...deliveredOrders].sort((a, b) => {
      // Get payment status
      const aHasPayments = (paymentCounts?.[a.id] || 0) > 0;
      const bHasPayments = (paymentCounts?.[b.id] || 0) > 0;

      // Priority 1: Orders with payments come first
      if (aHasPayments && !bHasPayments) {
        return -1;
      }
      if (bHasPayments && !aHasPayments) {
        return 1;
      }

      // Priority 2: Among orders with same payment status, sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [deliveredOrders, paymentCounts]);

  // Get seller initials
  const getSellerInitials = (sellerId: string): string => {
    if (!getSellerInfo) return "";

    const seller = getSellerInfo(sellerId);
    const firstInitial = seller.name ? seller.name.charAt(0) : "";
    const lastInitial = seller.lastname ? seller.lastname.charAt(0) : "";

    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  // Generate a consistent background color based on seller name
  const getSellerColor = (sellerId: string): { bg: string; text: string } => {
    if (!getSellerInfo) return { bg: "bg-blue-100", text: "text-blue-800" };

    const seller = getSellerInfo(sellerId);
    const fullName = `${seller.name} ${seller.lastname}`;

    // Generate a simple hash from the name
    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use hash to determine color from a predefined set
    const colorIndex = Math.abs(hash) % 10;

    const colors = [
      { bg: "bg-blue-100", text: "text-blue-800" },
      { bg: "bg-green-100", text: "text-green-800" },
      { bg: "bg-purple-100", text: "text-purple-800" },
      { bg: "bg-indigo-100", text: "text-indigo-800" },
      { bg: "bg-pink-100", text: "text-pink-800" },
      { bg: "bg-teal-100", text: "text-teal-800" },
      { bg: "bg-orange-100", text: "text-orange-800" },
      { bg: "bg-cyan-100", text: "text-cyan-800" },
      { bg: "bg-amber-100", text: "text-amber-800" },
      { bg: "bg-lime-100", text: "text-lime-800" },
    ];

    return colors[colorIndex];
  };

  // A card is "recently moved" only in the column matching its destination
  // status, so the arrival animation plays once — in the new column, not the old.
  const isRecentlyMoved = (order: SalesRequest) =>
    movedStatuses?.get(order.id) === order.status;

  const renderSalesNoteCard = (order: SalesRequest, isMoved = false) => (
    <Card
      key={order.id}
      className={cn(
        "mb-3 cursor-pointer shadow-sm transition-shadow hover:shadow-md",
        isMoved && "animate-card-move-in motion-reduce:animate-none"
      )}
      onClick={() => onOrderClick(order)}
    >
      <CardContent className="p-3">
        <div className="mb-2 flex items-center justify-between">
          {order.status !== "rejected" && (
            <span className="text-xs text-muted-foreground">
              {getDisplayId(order)}
            </span>
          )}
          <Badge
            variant={
              order.status === "rejected"
                ? "destructive"
                : order.paid
                  ? "default"
                  : "outline"
            }
            className={
              order.status === "rejected"
                ? "bg-red-100 text-red-800 hover:bg-red-100"
                : order.paid
                  ? "bg-green-100 text-green-800 hover:bg-green-100"
                  : ""
            }
          >
            {order.status === "rejected" && order.previousStatus
              ? `Anterior: ${getStatusLabel(order.previousStatus)}`
              : order.paid
                ? "Pagado"
                : "Pendiente de pago"}
          </Badge>
        </div>

        <div className="mb-1 flex items-center justify-between">
          <div className="mr-2 flex-1 truncate font-medium">
            <div className="flex items-center gap-1">
              <span>{clientNames[order.clientId] || "Cliente sin nombre"}</span>
              {clientInfo?.[order.clientId]?.isDeleted && (
                <Badge variant="destructive" className="px-1 py-0 text-xs">
                  <Trash2 className="mr-1 h-3 w-3" />
                  Eliminado
                </Badge>
              )}
              {order.isNonSale && (
                <Badge className="bg-amber-100 px-1 py-0 text-xs text-amber-800 hover:bg-amber-100">
                  No venta
                </Badge>
              )}
            </div>
          </div>

          {getSellerInfo && (
            <div
              className={`h-6 w-6 rounded-full ${getSellerColor(order.createdBy).bg} ${getSellerColor(order.createdBy).text} flex flex-shrink-0 items-center justify-center text-xs font-medium`}
              title={`${getSellerInfo(order.createdBy).name} ${getSellerInfo(order.createdBy).lastname}`}
            >
              {getSellerInitials(order.createdBy)}
            </div>
          )}
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{dayjs(order.createdAt).format("DD/MM/YYYY HH:mm")}</span>
            </div>
            <div className="font-medium">
              {formatCurrency(grossFromNet(order.totalAmount, order.isExento))}
            </div>
          </div>
          {order.deliveryDate && (
            <div className="flex items-center gap-1 text-xs text-blue-600">
              Entrega: {dayjs(order.deliveryDate).format("DD/MM/YYYY")}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Última actualización:{" "}
            {dayjs(order.updatedAt).format("DD/MM/YYYY HH:mm")}
          </div>

          {/* Payment count indicator */}
          <div className="text-xs text-muted-foreground">
            {(paymentCounts?.[order.id] || 0) > 0 ? (
              <span className="flex items-center gap-1">
                💳 {paymentCounts?.[order.id]} pago
                {paymentCounts?.[order.id] !== 1 ? "s" : ""}
              </span>
            ) : (
              <span className="text-gray-400">Sin pagos registrados</span>
            )}
          </div>

          {/* Transportista info */}
          {order.assigned_transportista && getTransportistaInfo && (
            <div className="text-xs">
              <div className="flex items-center gap-1 text-blue-600">
                🚛{" "}
                <span className="font-medium">
                  {(() => {
                    const transportista = getTransportistaInfo(
                      order.assigned_transportista
                    );
                    return `${transportista.name} ${transportista.lastname}`;
                  })()}
                </span>
                {(() => {
                  const transportista = getTransportistaInfo(
                    order.assigned_transportista
                  );
                  return transportista.phone ? (
                    <span className="text-muted-foreground">
                      • {transportista.phone}
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // Accepted-specific card renderer that uses merged client names
  const renderAcceptedSalesNoteCard = (
    order: SalesRequest,
    isMoved = false
  ) => (
    <Card
      key={order.id}
      className={cn(
        "mb-3 cursor-pointer shadow-sm transition-shadow hover:shadow-md",
        isMoved && "animate-card-move-in motion-reduce:animate-none"
      )}
      onClick={() => onOrderClick(order)}
    >
      <CardContent className="p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {getDisplayId(order)}
          </span>
          <Badge
            variant={order.paid ? "default" : "outline"}
            className={
              order.paid ? "bg-green-100 text-green-800 hover:bg-green-100" : ""
            }
          >
            {order.paid ? "Pagado" : "Pendiente de pago"}
          </Badge>
        </div>

        <div className="mb-1 flex items-center justify-between">
          <div className="mr-2 flex-1 truncate font-medium">
            <div className="flex items-center gap-1">
              <span>
                {mergedClientNames[order.clientId] || "Cliente sin nombre"}
              </span>
              {clientInfo?.[order.clientId]?.isDeleted && (
                <Badge variant="destructive" className="px-1 py-0 text-xs">
                  <Trash2 className="mr-1 h-3 w-3" />
                  Eliminado
                </Badge>
              )}
              {order.isNonSale && (
                <Badge className="bg-amber-100 px-1 py-0 text-xs text-amber-800 hover:bg-amber-100">
                  No venta
                </Badge>
              )}
            </div>
          </div>

          {getSellerInfo && (
            <div
              className={`h-6 w-6 rounded-full ${getSellerColor(order.createdBy).bg} ${getSellerColor(order.createdBy).text} flex flex-shrink-0 items-center justify-center text-xs font-medium`}
              title={`${getSellerInfo(order.createdBy).name} ${getSellerInfo(order.createdBy).lastname}`}
            >
              {getSellerInitials(order.createdBy)}
            </div>
          )}
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{dayjs(order.createdAt).format("DD/MM/YYYY HH:mm")}</span>
            </div>
            <div className="font-medium">
              {formatCurrency(grossFromNet(order.totalAmount, order.isExento))}
            </div>
          </div>
          {order.deliveryDate && (
            <div className="flex items-center gap-1 text-xs text-blue-600">
              Entrega: {dayjs(order.deliveryDate).format("DD/MM/YYYY")}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Última actualización:{" "}
            {dayjs(order.updatedAt).format("DD/MM/YYYY HH:mm")}
          </div>

          {/* Payment count indicator */}
          <div className="text-xs text-muted-foreground">
            {(paymentCounts?.[order.id] || 0) > 0 ? (
              <span className="flex items-center gap-1">
                💳 {paymentCounts?.[order.id]} pago
                {paymentCounts?.[order.id] !== 1 ? "s" : ""}
              </span>
            ) : (
              <span className="text-gray-400">Sin pagos registrados</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Prepaid-specific card renderer that uses merged client names
  const renderPrepaidSalesNoteCard = (order: SalesRequest, isMoved = false) => (
    <Card
      key={order.id}
      className={cn(
        "mb-3 cursor-pointer shadow-sm transition-shadow hover:shadow-md",
        isMoved && "animate-card-move-in motion-reduce:animate-none"
      )}
      onClick={() => onOrderClick(order)}
    >
      <CardContent className="p-3">
        <div className="mb-2 flex items-center justify-between">
          {order.status !== "rejected" && (
            <span className="text-xs text-muted-foreground">
              {getDisplayId(order)}
            </span>
          )}
          <Badge
            variant={
              order.status === "rejected"
                ? "destructive"
                : order.paid
                  ? "default"
                  : "outline"
            }
            className={
              order.status === "rejected"
                ? "bg-red-100 text-red-800 hover:bg-red-100"
                : order.paid
                  ? "bg-green-100 text-green-800 hover:bg-green-100"
                  : ""
            }
          >
            {order.status === "rejected" && order.previousStatus
              ? `Anterior: ${getStatusLabel(order.previousStatus)}`
              : order.paid
                ? "Pagado"
                : "Pendiente de pago"}
          </Badge>
        </div>

        <div className="mb-1 flex items-center justify-between">
          <div className="mr-2 flex-1 truncate font-medium">
            <div className="flex items-center gap-1">
              <span>
                {mergedClientNames[order.clientId] || "Cliente sin nombre"}
              </span>
              {clientInfo?.[order.clientId]?.isDeleted && (
                <Badge variant="destructive" className="px-1 py-0 text-xs">
                  <Trash2 className="mr-1 h-3 w-3" />
                  Eliminado
                </Badge>
              )}
              {order.isNonSale && (
                <Badge className="bg-amber-100 px-1 py-0 text-xs text-amber-800 hover:bg-amber-100">
                  No venta
                </Badge>
              )}
            </div>
          </div>

          {getSellerInfo && (
            <div
              className={`h-6 w-6 rounded-full ${getSellerColor(order.createdBy).bg} ${getSellerColor(order.createdBy).text} flex flex-shrink-0 items-center justify-center text-xs font-medium`}
              title={`${getSellerInfo(order.createdBy).name} ${getSellerInfo(order.createdBy).lastname}`}
            >
              {getSellerInitials(order.createdBy)}
            </div>
          )}
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{dayjs(order.createdAt).format("DD/MM/YYYY HH:mm")}</span>
            </div>
            <div className="font-medium">
              {formatCurrency(grossFromNet(order.totalAmount, order.isExento))}
            </div>
          </div>
          {order.deliveryDate && (
            <div className="flex items-center gap-1 text-xs text-blue-600">
              Entrega: {dayjs(order.deliveryDate).format("DD/MM/YYYY")}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Última actualización:{" "}
            {dayjs(order.updatedAt).format("DD/MM/YYYY HH:mm")}
          </div>

          {/* Payment count indicator */}
          <div className="text-xs text-muted-foreground">
            {(paymentCounts?.[order.id] || 0) > 0 ? (
              <span className="flex items-center gap-1">
                💳 {paymentCounts?.[order.id]} pago
                {paymentCounts?.[order.id] !== 1 ? "s" : ""}
              </span>
            ) : (
              <span className="text-gray-400">Sin pagos registrados</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Render column header with tooltip
  const renderColumnHeader = (
    title: string,
    tooltipKey: keyof typeof COLUMN_TOOLTIPS,
    color: string,
    count: number,
    status?: string
  ) => {
    const statusData = status ? getStatusData(status) : null;
    const isExporting = exportingStatus === status;

    return (
      <div
        className={`rounded-t-md p-3 ${color} flex min-h-[60px] items-center text-center font-medium text-white`}
      >
        <div className="flex w-full flex-col justify-between px-2">
          <div className="flex min-w-0 flex-1 justify-between gap-2">
            <span className="whitespace-pre-line text-sm leading-tight">
              {title}
            </span>
            <div className="flex items-center gap-x-4">
              <Badge
                variant="outline"
                className="flex-shrink-0 border-white text-white"
              >
                {count}
              </Badge>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-shrink-0">
                    <Info className="h-4 w-4 cursor-help opacity-75 transition-opacity hover:opacity-100" />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="z-50 max-w-xs border border-gray-700 bg-gray-900 text-white"
                  sideOffset={5}
                >
                  <p className="text-sm">{COLUMN_TOOLTIPS[tooltipKey]}</p>
                  {statusData && (
                    <div className="mt-2 text-xs">
                      <div>
                        Monto Neto: {formatCurrency(statusData.totalAmount)}
                      </div>
                      <div>
                        Monto Bruto: {formatCurrency(statusData.priceWithIva)}
                      </div>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (status && count > 0) {
                    handleExportColumn(status, title);
                  }
                }}
                className="h-6 w-6 p-0 text-white/75 hover:bg-white/10 hover:text-white"
                disabled={!status || count === 0 || isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          <div className="flex gap-1">
            {statusData && statusData.totalAmount > 0 && (
              <div className="mt-4 w-fit space-y-1 text-xs opacity-90">
                <div>
                  <span className="text-xs opacity-75">Neto:</span>
                  <span className="font-medium">
                    {" "}
                    {formatCurrency(statusData.totalAmount)}
                  </span>
                </div>
                <div>
                  <span className="text-xs opacity-75">Bruto: </span>
                  <span className="font-medium">
                    {" "}
                    {formatCurrency(statusData.priceWithIva)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render column header with filter for En Tránsito
  const renderInTransitColumnHeader = (
    title: string,
    tooltipKey: keyof typeof COLUMN_TOOLTIPS,
    color: string,
    count: number,
    status?: string
  ) => {
    const statusData = status ? getStatusData(status) : null;
    const isExporting = exportingStatus === status;

    return (
      <div
        className={`rounded-t-md p-3 ${color} flex min-h-[60px] items-center text-center font-medium text-white`}
      >
        <div className="flex w-full flex-col gap-2 px-2">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="whitespace-pre-line text-sm leading-tight">
                {title}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-shrink-0">
                    <Info className="h-4 w-4 cursor-help opacity-75 transition-opacity hover:opacity-100" />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="z-50 max-w-xs border border-gray-700 bg-gray-900 text-white"
                  sideOffset={5}
                >
                  <p className="text-sm">{COLUMN_TOOLTIPS[tooltipKey]}</p>
                  {statusData && (
                    <div className="mt-2 text-xs">
                      <div>
                        Monto Neto: {formatCurrency(statusData.totalAmount)}
                      </div>
                      <div>
                        Monto Bruto: {formatCurrency(statusData.priceWithIva)}
                      </div>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (status && count > 0) {
                    handleExportColumn(status, title);
                  }
                }}
                className="h-6 w-6 p-0 text-white/75 hover:bg-white/10 hover:text-white"
                disabled={!status || count === 0 || isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge
                variant="outline"
                className="flex-shrink-0 border-white text-white"
              >
                {count}
              </Badge>
              {statusData && statusData.totalAmount > 0 && (
                <div className="text-center text-xs opacity-90">
                  <div>Neto: {formatCurrency(statusData.totalAmount)}</div>
                  <div className="text-xs opacity-75">
                    Bruto: {formatCurrency(statusData.priceWithIva)}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={inTransitFilter === "all" ? "secondary" : "ghost"}
              onClick={() => setInTransitFilter("all")}
              className={`h-6 px-2 text-xs ${
                inTransitFilter === "all"
                  ? "bg-white/20 text-white hover:bg-white/30"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              Todos
            </Button>
            <Button
              size="sm"
              variant={inTransitFilter === "unpaid" ? "secondary" : "ghost"}
              onClick={() => setInTransitFilter("unpaid")}
              className={`h-6 px-2 text-xs ${
                inTransitFilter === "unpaid"
                  ? "bg-white/20 text-white hover:bg-white/30"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              No Pagado
            </Button>
            <Button
              size="sm"
              variant={inTransitFilter === "paid" ? "secondary" : "ghost"}
              onClick={() => setInTransitFilter("paid")}
              className={`h-6 px-2 text-xs ${
                inTransitFilter === "paid"
                  ? "bg-white/20 text-white hover:bg-white/30"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              Pagado
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Skeleton placeholder for a single Kanban card, mirrors renderSalesNoteCard
  const renderCardSkeleton = (key: number) => (
    <Card key={`skeleton-${key}`} className="mb-3 shadow-sm">
      <CardContent className="p-3">
        <div className="mb-2 flex items-center justify-between">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
        <div className="mb-1 flex items-center justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-6 w-6 rounded-full" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </CardContent>
    </Card>
  );

  // Renders a set of skeleton cards while a column's data is loading
  const renderColumnSkeletons = (count = 3) => (
    <>{Array.from({ length: count }).map((_, i) => renderCardSkeleton(i))}</>
  );

  const renderLazyColumn = (
    title: string,
    orders: SalesRequest[],
    color: string,
    columnKey: keyof typeof visibleCounts,
    tooltipKey: keyof typeof COLUMN_TOOLTIPS,
    status?: string
  ) => {
    const visibleCount = visibleCounts[columnKey];
    const displayedOrders = orders.slice(0, visibleCount);
    const hasMore = orders.length > visibleCount;

    return (
      <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
        {renderColumnHeader(title, tooltipKey, color, orders.length, status)}
        <div
          className={cn(
            "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
            hasBoardLoaded && "duration-300 animate-in fade-in-0"
          )}
        >
          {!hasBoardLoaded || (isLoading && orders.length === 0) ? (
            renderColumnSkeletons()
          ) : orders.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No hay notas en este estado
            </div>
          ) : (
            <>
              {displayedOrders.map((order) =>
                renderSalesNoteCard(order, isRecentlyMoved(order))
              )}
              {hasMore && (
                <div className="mt-3 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadMore(columnKey)}
                    className="text-xs"
                  >
                    <ChevronDown className="mr-1 h-3 w-3" />
                    Ver más ({orders.length - visibleCount})
                  </Button>
                </div>
              )}
              {visibleCount >= orders.length &&
                orders.length > LAZY_LOAD_CONFIG[columnKey].initial && (
                  <div className="mt-3 text-center text-xs text-muted-foreground">
                    Mostrando todos los registros
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-[calc(100vh-180px)] flex-col sm:h-[calc(100vh-220px)]">
        <div
          ref={topScrollRef}
          onScroll={() =>
            syncScroll(topScrollRef.current, boardScrollRef.current)
          }
          className="mb-3 shrink-0 overflow-x-auto overflow-y-hidden [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-muted-foreground/50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-2"
        >
          <div className="h-px" style={{ width: boardScrollWidth }} />
        </div>
        <div
          ref={boardScrollRef}
          onScroll={() =>
            syncScroll(boardScrollRef.current, topScrollRef.current)
          }
          className="flex min-h-0 flex-1 snap-x snap-mandatory space-x-3 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {isTransportista ? (
            // Transportista: "En Tránsito", "Entregado", and "Rechazada"
            <>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderInTransitColumnHeader(
                    "En\nTránsito",
                    "in_transit",
                    "bg-blue-600",
                    inTransitOrders.length,
                    "in_transit"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded ||
                    (isLoading &&
                      sortedAndFilteredInTransitOrders.length === 0) ? (
                      renderColumnSkeletons()
                    ) : sortedAndFilteredInTransitOrders.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {sortedAndFilteredInTransitOrders
                          .slice(0, visibleCounts.in_transit)
                          .map((order) =>
                            renderSalesNoteCard(order, isRecentlyMoved(order))
                          )}
                        {sortedAndFilteredInTransitOrders.length >
                          visibleCounts.in_transit && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadMore("in_transit")}
                              className="text-xs"
                            >
                              <ChevronDown className="mr-1 h-3 w-3" />
                              Ver más (
                              {sortedAndFilteredInTransitOrders.length -
                                visibleCounts.in_transit}
                              )
                            </Button>
                          </div>
                        )}
                        {visibleCounts.in_transit >=
                          sortedAndFilteredInTransitOrders.length &&
                          sortedAndFilteredInTransitOrders.length >
                            LAZY_LOAD_CONFIG.in_transit.initial && (
                            <div className="mt-3 text-center text-xs text-muted-foreground">
                              Mostrando todos los registros
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Entregado\n ",
                  sortedDeliveredOrders,
                  "bg-blue-800",
                  "delivered",
                  "delivered",
                  "delivered"
                )}
              </div>
              {/* Deprioritized column - less prominent */}
              <div className="snap-start opacity-75">
                {renderLazyColumn(
                  "Rechazada\n ",
                  rejectedOrders,
                  "bg-red-500", // Slightly less intense red
                  "rejected",
                  "rejected",
                  "rejected"
                )}
              </div>
            </>
          ) : isLogistica ? (
            // Logística: "Facturado", "Transito", "Entregado", "Rechazado"
            <>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Por Facturar",
                    "por_facturar",
                    "bg-blue-600",
                    porFacturarNotes.length,
                    "por_facturar"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isPorFacturarLoading ? (
                      renderColumnSkeletons()
                    ) : porFacturarNotes.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {porFacturarNotes.map((order) =>
                          renderAcceptedSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasPorFacturarMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMorePorFacturarNotes}
                              disabled={isPorFacturarLoadingMore}
                              className="text-xs"
                            >
                              {isPorFacturarLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isPorFacturarLoadingMore
                                ? "Cargando..."
                                : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Facturado\n ",
                  facturadoOrders,
                  "bg-indigo-500",
                  "facturado",
                  "facturado",
                  "facturado"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderInTransitColumnHeader(
                    "En\nTránsito",
                    "in_transit",
                    "bg-blue-600",
                    inTransitOrders.length,
                    "in_transit"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded ||
                    (isLoading &&
                      sortedAndFilteredInTransitOrders.length === 0) ? (
                      renderColumnSkeletons()
                    ) : sortedAndFilteredInTransitOrders.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {sortedAndFilteredInTransitOrders
                          .slice(0, visibleCounts.in_transit)
                          .map((order) =>
                            renderSalesNoteCard(order, isRecentlyMoved(order))
                          )}
                        {sortedAndFilteredInTransitOrders.length >
                          visibleCounts.in_transit && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadMore("in_transit")}
                              className="text-xs"
                            >
                              <ChevronDown className="mr-1 h-3 w-3" />
                              Ver más (
                              {sortedAndFilteredInTransitOrders.length -
                                visibleCounts.in_transit}
                              )
                            </Button>
                          </div>
                        )}
                        {visibleCounts.in_transit >=
                          sortedAndFilteredInTransitOrders.length &&
                          sortedAndFilteredInTransitOrders.length >
                            LAZY_LOAD_CONFIG.in_transit.initial && (
                            <div className="mt-3 text-center text-xs text-muted-foreground">
                              Mostrando todos los registros
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Entregado\n ",
                  sortedDeliveredOrders,
                  "bg-blue-800",
                  "delivered",
                  "delivered",
                  "delivered"
                )}
              </div>
              {/* Deprioritized column - less prominent */}
              <div className="snap-start opacity-75">
                {renderLazyColumn(
                  "Rechazada\n ",
                  rejectedOrders,
                  "bg-red-500", // Slightly less intense red
                  "rejected",
                  "rejected",
                  "rejected"
                )}
              </div>
            </>
          ) : isCobranza ? (
            // Cobranza: "Autorización Cobranza", "Anticipado Pdte Pago", "Validación Pago", "Aprobadas Cobranza", "Facturado", "Transito", "Entregado", "Rechazado"
            <>
              <div className="snap-start">
                {renderLazyColumn(
                  "Autorización\nCobranza",
                  pendingOrders,
                  "bg-amber-500",
                  "pending",
                  "pending",
                  "pending"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Anticipado\nPendiente de Pago",
                    "prepaid",
                    "bg-orange-500",
                    prepaidNotes.length,
                    "prepaid"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isPrepaidLoading ? (
                      renderColumnSkeletons()
                    ) : prepaidOrders.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {prepaidOrders.map((order) =>
                          renderPrepaidSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasPrepaidMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMorePrepaidNotes}
                              disabled={isPrepaidLoadingMore}
                              className="text-xs"
                            >
                              {isPrepaidLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isPrepaidLoadingMore ? "Cargando..." : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Validación\nPago",
                  allPaymentValidationOrders,
                  "bg-yellow-500",
                  "payment_to_check",
                  "payment_to_check",
                  "payment_to_check"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Aprobada\n ",
                    "accepted",
                    "bg-green-600",
                    acceptedNotes.length,
                    "accepted"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isAcceptedLoading ? (
                      renderColumnSkeletons()
                    ) : acceptedNotes.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {acceptedNotes.map((order) =>
                          renderAcceptedSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasAcceptedMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMoreAcceptedNotes}
                              disabled={isAcceptedLoadingMore}
                              className="text-xs"
                            >
                              {isAcceptedLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isAcceptedLoadingMore
                                ? "Cargando..."
                                : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Por Facturar",
                    "por_facturar",
                    "bg-blue-600",
                    porFacturarNotes.length,
                    "por_facturar"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isPorFacturarLoading ? (
                      renderColumnSkeletons()
                    ) : porFacturarNotes.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {porFacturarNotes.map((order) =>
                          renderAcceptedSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasPorFacturarMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMorePorFacturarNotes}
                              disabled={isPorFacturarLoadingMore}
                              className="text-xs"
                            >
                              {isPorFacturarLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isPorFacturarLoadingMore
                                ? "Cargando..."
                                : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Facturado\n ",
                  facturadoOrders,
                  "bg-indigo-500",
                  "facturado",
                  "facturado",
                  "facturado"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderInTransitColumnHeader(
                    "En\nTránsito",
                    "in_transit",
                    "bg-blue-600",
                    inTransitOrders.length,
                    "in_transit"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded ||
                    (isLoading &&
                      sortedAndFilteredInTransitOrders.length === 0) ? (
                      renderColumnSkeletons()
                    ) : sortedAndFilteredInTransitOrders.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {sortedAndFilteredInTransitOrders
                          .slice(0, visibleCounts.in_transit)
                          .map((order) =>
                            renderSalesNoteCard(order, isRecentlyMoved(order))
                          )}
                        {sortedAndFilteredInTransitOrders.length >
                          visibleCounts.in_transit && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadMore("in_transit")}
                              className="text-xs"
                            >
                              <ChevronDown className="mr-1 h-3 w-3" />
                              Ver más (
                              {sortedAndFilteredInTransitOrders.length -
                                visibleCounts.in_transit}
                              )
                            </Button>
                          </div>
                        )}
                        {visibleCounts.in_transit >=
                          sortedAndFilteredInTransitOrders.length &&
                          sortedAndFilteredInTransitOrders.length >
                            LAZY_LOAD_CONFIG.in_transit.initial && (
                            <div className="mt-3 text-center text-xs text-muted-foreground">
                              Mostrando todos los registros
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Entregado\n ",
                  sortedDeliveredOrders,
                  "bg-blue-800",
                  "delivered",
                  "delivered",
                  "delivered"
                )}
              </div>
              {/* Deprioritized column - less prominent */}
              <div className="snap-start opacity-75">
                {renderLazyColumn(
                  "Rechazada\n ",
                  rejectedOrders,
                  "bg-red-500", // Slightly less intense red
                  "rejected",
                  "rejected",
                  "rejected"
                )}
              </div>
            </>
          ) : isFacturacion ? (
            // Facturación: "Autorización Precio", "Aprobadas Cobranza", "Facturado", "Transito", "Entregado", "Rechazado"
            <>
              <div className="snap-start">
                {renderLazyColumn(
                  "Autorización\npor Precio",
                  pendingPricingOrders,
                  "bg-purple-600",
                  "pending_pricing",
                  "pending_pricing",
                  "pending_pricing"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Aprobada\n ",
                    "accepted",
                    "bg-green-600",
                    acceptedNotes.length,
                    "accepted"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isAcceptedLoading ? (
                      renderColumnSkeletons()
                    ) : acceptedNotes.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {acceptedNotes.map((order) =>
                          renderAcceptedSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasAcceptedMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMoreAcceptedNotes}
                              disabled={isAcceptedLoadingMore}
                              className="text-xs"
                            >
                              {isAcceptedLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isAcceptedLoadingMore
                                ? "Cargando..."
                                : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Por Facturar",
                    "por_facturar",
                    "bg-blue-600",
                    porFacturarNotes.length,
                    "por_facturar"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isPorFacturarLoading ? (
                      renderColumnSkeletons()
                    ) : porFacturarNotes.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {porFacturarNotes.map((order) =>
                          renderAcceptedSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasPorFacturarMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMorePorFacturarNotes}
                              disabled={isPorFacturarLoadingMore}
                              className="text-xs"
                            >
                              {isPorFacturarLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isPorFacturarLoadingMore
                                ? "Cargando..."
                                : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Facturado\n ",
                  facturadoOrders,
                  "bg-indigo-500",
                  "facturado",
                  "facturado",
                  "facturado"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderInTransitColumnHeader(
                    "En\nTránsito",
                    "in_transit",
                    "bg-blue-600",
                    inTransitOrders.length,
                    "in_transit"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded ||
                    (isLoading &&
                      sortedAndFilteredInTransitOrders.length === 0) ? (
                      renderColumnSkeletons()
                    ) : sortedAndFilteredInTransitOrders.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {sortedAndFilteredInTransitOrders
                          .slice(0, visibleCounts.in_transit)
                          .map((order) =>
                            renderSalesNoteCard(order, isRecentlyMoved(order))
                          )}
                        {sortedAndFilteredInTransitOrders.length >
                          visibleCounts.in_transit && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadMore("in_transit")}
                              className="text-xs"
                            >
                              <ChevronDown className="mr-1 h-3 w-3" />
                              Ver más (
                              {sortedAndFilteredInTransitOrders.length -
                                visibleCounts.in_transit}
                              )
                            </Button>
                          </div>
                        )}
                        {visibleCounts.in_transit >=
                          sortedAndFilteredInTransitOrders.length &&
                          sortedAndFilteredInTransitOrders.length >
                            LAZY_LOAD_CONFIG.in_transit.initial && (
                            <div className="mt-3 text-center text-xs text-muted-foreground">
                              Mostrando todos los registros
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Entregado\n ",
                  sortedDeliveredOrders,
                  "bg-blue-800",
                  "delivered",
                  "delivered",
                  "delivered"
                )}
              </div>
              {/* Deprioritized column - less prominent */}
              <div className="snap-start opacity-75">
                {renderLazyColumn(
                  "Rechazada\n ",
                  rejectedOrders,
                  "bg-red-500", // Slightly less intense red
                  "rejected",
                  "rejected",
                  "rejected"
                )}
              </div>
            </>
          ) : (
            // Show ALL workflow status columns for all other users (sellers, admin, cobranza, etc.)
            // Sellers can see all columns but only their own sales notes in each column
            <>
              <div className="snap-start">
                {renderLazyColumn(
                  "Autorización\npor Precio",
                  pendingPricingOrders,
                  "bg-purple-600",
                  "pending_pricing",
                  "pending_pricing",
                  "pending_pricing"
                )}
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Autorización\nCobranza",
                  pendingOrders,
                  "bg-amber-500",
                  "pending",
                  "pending",
                  "pending"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Anticipado\nPendiente de Pago",
                    "prepaid",
                    "bg-orange-500",
                    prepaidNotes.length,
                    "prepaid"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isPrepaidLoading ? (
                      renderColumnSkeletons()
                    ) : prepaidOrders.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {prepaidOrders.map((order) =>
                          renderPrepaidSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasPrepaidMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMorePrepaidNotes}
                              disabled={isPrepaidLoadingMore}
                              className="text-xs"
                            >
                              {isPrepaidLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isPrepaidLoadingMore ? "Cargando..." : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Validación\nPago",
                  allPaymentValidationOrders,
                  "bg-yellow-500",
                  "payment_to_check",
                  "payment_to_check",
                  "payment_to_check"
                )}
              </div>

              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Aprobada\n ",
                    "accepted",
                    "bg-green-600",
                    acceptedNotes.length,
                    "accepted"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isAcceptedLoading ? (
                      renderColumnSkeletons()
                    ) : acceptedNotes.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {acceptedNotes.map((order) =>
                          renderAcceptedSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasAcceptedMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMoreAcceptedNotes}
                              disabled={isAcceptedLoadingMore}
                              className="text-xs"
                            >
                              {isAcceptedLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isAcceptedLoadingMore
                                ? "Cargando..."
                                : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderColumnHeader(
                    "Por Facturar",
                    "por_facturar",
                    "bg-blue-600",
                    porFacturarNotes.length,
                    "por_facturar"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded || isPorFacturarLoading ? (
                      renderColumnSkeletons()
                    ) : porFacturarNotes.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {porFacturarNotes.map((order) =>
                          renderAcceptedSalesNoteCard(
                            order,
                            isRecentlyMoved(order)
                          )
                        )}
                        {hasPorFacturarMore && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMorePorFacturarNotes}
                              disabled={isPorFacturarLoadingMore}
                              className="text-xs"
                            >
                              {isPorFacturarLoadingMore ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              {isPorFacturarLoadingMore
                                ? "Cargando..."
                                : "Ver más"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Facturado\n ",
                  facturadoOrders,
                  "bg-indigo-500",
                  "facturado",
                  "facturado",
                  "facturado"
                )}
              </div>
              <div className="snap-start">
                <div className="w-[85vw] min-w-[250px] flex-shrink-0 sm:w-[320px] md:w-auto md:min-w-[250px] md:max-w-[300px]">
                  {renderInTransitColumnHeader(
                    "En\nTránsito",
                    "in_transit",
                    "bg-blue-600",
                    inTransitOrders.length,
                    "in_transit"
                  )}
                  <div
                    className={cn(
                      "h-[calc(100%-60px)] overflow-y-auto rounded-b-md bg-slate-50 p-2",
                      hasBoardLoaded && "duration-300 animate-in fade-in-0"
                    )}
                  >
                    {!hasBoardLoaded ||
                    (isLoading &&
                      sortedAndFilteredInTransitOrders.length === 0) ? (
                      renderColumnSkeletons()
                    ) : sortedAndFilteredInTransitOrders.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No hay notas en este estado
                      </div>
                    ) : (
                      <>
                        {sortedAndFilteredInTransitOrders
                          .slice(0, visibleCounts.in_transit)
                          .map((order) =>
                            renderSalesNoteCard(order, isRecentlyMoved(order))
                          )}
                        {sortedAndFilteredInTransitOrders.length >
                          visibleCounts.in_transit && (
                          <div className="mt-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadMore("in_transit")}
                              className="text-xs"
                            >
                              <ChevronDown className="mr-1 h-3 w-3" />
                              Ver más (
                              {sortedAndFilteredInTransitOrders.length -
                                visibleCounts.in_transit}
                              )
                            </Button>
                          </div>
                        )}
                        {visibleCounts.in_transit >=
                          sortedAndFilteredInTransitOrders.length &&
                          sortedAndFilteredInTransitOrders.length >
                            LAZY_LOAD_CONFIG.in_transit.initial && (
                            <div className="mt-3 text-center text-xs text-muted-foreground">
                              Mostrando todos los registros
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="snap-start">
                {renderLazyColumn(
                  "Entregado\n ",
                  sortedDeliveredOrders,
                  "bg-blue-800",
                  "delivered",
                  "delivered",
                  "delivered"
                )}
              </div>
              {/* Deprioritized columns - less prominent */}
              <div className="snap-start opacity-75">
                {renderLazyColumn(
                  "Rechazada\n ",
                  rejectedOrders,
                  "bg-red-500", // Slightly less intense red
                  "rejected",
                  "rejected",
                  "rejected"
                )}
              </div>
              <div className="snap-start opacity-75">
                {renderLazyColumn(
                  "Test\n ",
                  testOrders,
                  "bg-gray-400", // Lighter gray
                  "test",
                  "test",
                  "test"
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default KanbanView;
